import { wireCopyButton } from "./copy-button.js";
import { plainFlavours } from "./copy-html.js";
import { debounced } from "./debounce.js";
import { replaceChildren, slot } from "./dom.js";
import { ListPicker } from "./list-picker.js";
import { OwnListSelect, SavedChoices } from "./saved-choices.js";
import { savedParts, savedTags, taggedWords } from "./saved-parts.js";
import { WorksheetEntries } from "./worksheet-entries.js";
import { freshSeed } from "./worksheet-seed.js";
import { paperSizes } from "./worksheet-layout.js";
import type { SheetLabels } from "./worksheet-page.js";
import {
  type ListWords,
  type OwnListChoice,
  type SavedWord,
  WorksheetSources,
} from "./worksheet-sources.js";
import type { SheetSourceState, WorksheetPaper } from "./worksheet-state.js";
import { typedWords } from "./worksheet-words.js";

/**
 * A worksheet generator: the controls, the preview and the print.
 *
 * Every change redraws the sheet and writes the settings into the address, so
 * what is on screen, what the address says and what prints are always the
 * same sheet. The preview is the sheet itself, scaled to fit the column, and
 * printing hides the rest of the page around it.
 *
 * Each kind of sheet is a `SheetKind`: how its settings read and write, and
 * how a sheet is drawn from a list of words. Everything else, where the words
 * come from included, is this module's and is the same for all of them. The
 * sentence sheet asks for 例句 in place of words, by `material`, and the same
 * sources give it their sentences: a List's, an own List's and the reader's
 * saved ones.
 */

/** How many requests a sheet has in flight at once. */
export const concurrency = 12;

/** What the printed footer names the site as. */
const siteLabel = "chineseboost.com";

/** What a kind of sheet is asked to draw. */
export interface SheetRequest<State extends SheetSourceState> {
  readonly state: State;

  /**
   * The words, in the order they were given. Never empty for a kind of
   * sheet drawn from words, and the source's words, perhaps none, for one
   * drawn from sentences.
   */
  readonly words: readonly string[];

  /**
   * The 例句 by their ids, in the order they were given. Never empty for a
   * kind of sheet drawn from sentences. For one drawn from words, a List's or
   * an own List's 例句, which the revision booklet prints after the words,
   * and none for the other sources.
   */
  readonly sentences: readonly string[];

  readonly entries: WorksheetEntries;
  readonly labels: SheetLabels;
}

/** A drawn sheet, and the line the status says about it. */
export interface DrawnSheet {
  readonly nodes: readonly Node[];
  readonly status: string;
}

/** One kind of sheet. */
export interface SheetKind<State extends SheetSourceState> {
  /** Read the settings out of an address fragment. */
  read(hash: string): State;

  /** The fragment for the settings, holding what differs from the defaults. */
  fragment(state: State): string;

  /** What to say when the settings ask for nothing to be printed, if they do. */
  nothingAsked(state: State): string | undefined;

  /**
   * What the sheet is drawn from: a source's words, or its 例句. The saved
   * source is the reader's saved words for the one and their saved 例句 for
   * the other. Words where this is left out.
   */
  readonly material?: "words" | "sentences";

  /** What a sheet is called, from where its words came. */
  title(state: State, list: ListWords | undefined): string;

  /**
   * Draw the sheet.
   * @returns The sheet, or what to say instead where there is nothing to draw.
   */
  draw(request: SheetRequest<State>): Promise<DrawnSheet | string>;

  /**
   * Whether the settings move any of those under More settings, which opens
   * for an address that does. Every kind has the order there, and a kind with
   * more settings of its own says so here.
   */
  changesMore?(state: State): boolean;
}

/** Every control that names a setting, by the setting it names. */
function fields(container: HTMLElement): readonly HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>("[data-field]")];
}

/** What the controls say, laid over the settings they do not name. */
function readControls<State extends SheetSourceState>(
  container: HTMLElement,
  kind: SheetKind<State>,
  current: State,
): State {
  const parameters = new URLSearchParams(kind.fragment(current));
  const text = slot(container, "text");
  const list = slot(container, "list");

  for (const field of fields(container)) {
    const name = field.dataset["field"]!;
    if (field instanceof HTMLInputElement && field.type === "checkbox") {
      parameters.set(name, field.checked ? "1" : "0");
    } else if (
      field instanceof HTMLSelectElement ||
      field instanceof HTMLInputElement
    ) {
      parameters.set(name, field.value);
    } else {
      const checked = field.querySelector<HTMLInputElement>("input:checked");
      if (checked !== null) {
        parameters.set(name, checked.value);
      }
    }
  }
  if (text instanceof HTMLTextAreaElement) {
    parameters.set("text", text.value);
  }
  if (list instanceof HTMLSelectElement) {
    parameters.set("list", list.value);
  }
  const tag = slot(container, "tag");
  const mine = slot(container, "mine");
  const part = slot(container, "part");
  if (tag instanceof HTMLSelectElement && tag.options.length > 0) {
    parameters.set("tag", tag.value);
  }
  if (mine instanceof HTMLSelectElement && mine.options.length > 0) {
    parameters.set("mine", mine.value);
  }
  // A part is a part of one tag's words or one List's, so choosing another
  // starts again from the first.
  const band = container.querySelector<HTMLSelectElement>(
    'select[data-field="band"]',
  );
  const same =
    (band === null || band.value === current.band) &&
    (!(tag instanceof HTMLSelectElement) ||
      tag.options.length === 0 ||
      tag.value === current.tag) &&
    (!(mine instanceof HTMLSelectElement) ||
      mine.options.length === 0 ||
      mine.value === current.mine);
  if (part instanceof HTMLSelectElement && part.options.length > 0) {
    parameters.set("part", same ? part.value : "1");
  }

  return kind.read(parameters.toString());
}

/** Put the settings into the controls. */
function writeControls(container: HTMLElement, state: SheetSourceState): void {
  const values = state as unknown as Readonly<Record<string, unknown>>;

  for (const field of fields(container)) {
    const value = values[field.dataset["field"]!];
    if (field instanceof HTMLInputElement && field.type === "checkbox") {
      field.checked = value === true;
    } else if (
      field instanceof HTMLSelectElement ||
      field instanceof HTMLInputElement
    ) {
      field.value = String(value);
    } else {
      for (const radio of field.querySelectorAll<HTMLInputElement>("input")) {
        radio.checked = radio.value === String(value);
      }
    }
  }
  const text = slot(container, "text");
  if (text instanceof HTMLTextAreaElement) {
    text.value = state.text;
  }
  const list = slot(container, "list");
  if (list instanceof HTMLSelectElement && state.list !== "") {
    list.value = state.list;
  }
  for (const panel of container.querySelectorAll<HTMLElement>(
    "[data-cb-source-panel]",
  )) {
    panel.hidden = panel.dataset["cbSourcePanel"] !== state.source;
  }
  const reshuffle = slot(container, "reshuffle");
  if (reshuffle !== undefined) {
    reshuffle.hidden = state.order !== "shuffled";
  }
}

/**
 * Whether the page lists a List as too large to print whole.
 *
 * Hugo disables its option in the List select, by
 * `partials/lists/prints-whole.html`. An address can still name one, from a
 * link made before the rule or written by hand.
 */
function tooLarge(container: HTMLElement, list: string): boolean {
  const select = slot(container, "list");

  return (
    select instanceof HTMLSelectElement &&
    [...select.options].some(
      (option) => option.value === list && option.disabled,
    )
  );
}

/**
 * Mark the way from the page down to the sheet, so printing can hide
 * everything off it.
 *
 * Print CSS cannot say "everything but this" on its own without `:has()`,
 * and the site's chrome is several layers deep around the article. Each
 * ancestor of the sheet gets `data-cb-print-path`, and the stylesheet hides
 * every sibling along that path. See `modules/worksheet`.
 */
function markPrintPath(sheet: HTMLElement): void {
  sheet.setAttribute("data-cb-print-root", "");
  for (
    let parent = sheet.parentElement;
    parent !== null;
    parent = parent.parentElement
  ) {
    parent.setAttribute("data-cb-print-path", "");
  }
}

/**
 * Tell the print dialog which paper the sheet is laid out for.
 *
 * Chrome's dialog takes its paper size and margins from the unnamed `@page`,
 * and the named pages in `modules/worksheet` do not move it. Left alone it
 * offered US Letter with its own margins, header and footer, and every page
 * of an A4 sheet ran onto a second sheet of paper. The rule is written into
 * a constructed stylesheet, which the Content-Security-Policy allows where it
 * refuses an inline `<style>`, and only this page ever adopts it, so the rest
 * of the site prints as it did.
 * @returns What sets the rule for a paper, which does nothing in a browser
 * without constructed stylesheets.
 */
function printPaper(document: Document): (paper: WorksheetPaper) => void {
  if (
    typeof CSSStyleSheet === "undefined" ||
    !("replaceSync" in CSSStyleSheet.prototype) ||
    !("adoptedStyleSheets" in document)
  ) {
    return () => {};
  }
  const sheet = new CSSStyleSheet();
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];

  return (paper) => {
    sheet.replaceSync(
      `@page { size: ${paper === "a4" ? "A4" : "letter"} portrait; margin: 0; }`,
    );
  };
}

/** What a source gives a sheet, and what its title adds. */
interface Found {
  readonly words: readonly string[];
  readonly sentences: readonly string[];
  readonly list?: ListWords;
  readonly suffix?: string;
}

/** What the starter needs from the page. */
export interface SheetToolProperties {
  readonly fetch: typeof fetch;

  /** Where a shuffle's seeds come from. `Math.random` on a page. */
  readonly random?: () => number;
}

/** The `fetch` a page gives a tool when nothing else is asked for. */
export const pageFetch: SheetToolProperties = {
  fetch: (...parameters) => fetch(...parameters),
};

/** Start a generator of one kind of sheet on its container. */
export function startSheetTool<State extends SheetSourceState>(
  container: HTMLElement,
  kind: SheetKind<State>,
  properties: SheetToolProperties,
): void {
  const preview = slot(container, "preview");
  const sheet = slot(container, "sheet");
  const status = slot(container, "status");
  if (preview === undefined || sheet === undefined) {
    return;
  }

  const entries = new WorksheetEntries({
    fetch: properties.fetch,
    strokesUrl: container.dataset["strokes"] ?? "",
  });
  const sources = new WorksheetSources({ fetch: properties.fetch });
  const picker = ListPicker.on(container);
  const choices = SavedChoices.on(container);
  const ownLists = OwnListSelect.on(container);
  // The saved words are read once a page, the first time a sheet asks, and
  // read again only after a failure. A tag or a part chooses among them.
  let saved: Promise<readonly SavedWord[] | undefined> | undefined;
  const savedWords = async (): Promise<readonly SavedWord[] | undefined> => {
    saved ??= sources.saved();
    const words = await saved;
    if (words === undefined) {
      saved = undefined;
    }

    return words;
  };
  // The reader's saved 例句, for a sheet drawn from sentences, read the same
  // way.
  let savedLiju: Promise<readonly string[] | undefined> | undefined;
  const savedSentences = async (): Promise<readonly string[] | undefined> => {
    savedLiju ??= sources.savedSentences();
    const sentences = await savedLiju;
    if (sentences === undefined) {
      savedLiju = undefined;
    }

    return sentences;
  };
  const material = kind.material ?? "words";
  // The reader's own Lists, read the same way.
  let shelf: Promise<readonly OwnListChoice[] | undefined> | undefined;
  const ownShelf = async (): Promise<readonly OwnListChoice[] | undefined> => {
    shelf ??= sources.ownLists();
    const lists = await shelf;
    if (lists === undefined) {
      shelf = undefined;
    }

    return lists;
  };

  /** The words cut into parts, the part chosen, and what the title adds. */
  const inParts = (
    words: readonly string[],
    before: string,
  ): { words: readonly string[]; suffix: string } => {
    const parts = savedParts(words);
    const part = Math.min(state.part, parts.length);
    choices?.parts(
      parts.map((each) => each.length),
      part,
      material,
    );

    return {
      words: parts[part - 1] ?? [],
      suffix: `${before}${parts.length < 2 ? "" : ` · part ${String(part)} of ${String(parts.length)}`}`,
    };
  };

  /**
   * A source's words and 例句 as the kind asks for them. A sheet drawn from
   * sentences takes its part of the 例句, whatever the source, since a
   * published List of a hundred words can hold twice as many. The words come
   * along to say which word in each sentence to ask about.
   */
  const fromList = (list: ListWords, parted: boolean): Found => {
    if (material === "sentences") {
      const { words: sentences, suffix } = inParts(list.sentences, "");

      return { words: list.words, sentences, list, suffix };
    }

    return parted
      ? { ...inParts(list.words, ""), sentences: list.sentences, list }
      : { words: list.words, sentences: list.sentences, list };
  };
  const write = (settings: State): void => {
    writeControls(container, settings);
    picker?.sync();
  };
  let state = kind.read(location.hash);
  let drawing = 0;

  markPrintPath(sheet);
  const more = slot(container, "more");
  if (
    more instanceof HTMLDetailsElement &&
    (state.order !== "given" || kind.changesMore?.(state) === true)
  ) {
    more.open = true;
  }
  const paper = printPaper(document);

  const say = (message: string): void => {
    if (status !== undefined) {
      status.textContent = message;
      status.hidden = message === "";
    }
  };

  const fitPreview = (): void => {
    const room = preview.clientWidth;
    // 96 CSS pixels to the inch is what the browser lays a millimetre out at.
    const page = (paperSizes[state.paper].width / 25.4) * 96;
    sheet.style.setProperty(
      "--cb-ws-zoom",
      String(room > 0 ? Math.min(1, room / page) : 1),
    );
  };

  /**
   * The words a sheet is drawn from, or what to say instead.
   *
   * A reader's own words arrive after a request, and a newer draw may have
   * started by then. A stale answer leaves the controls alone, since they
   * belong to the newer draw, and `draw` throws the answer away.
   * @param current - Whether the draw asking is still the latest.
   */
  const words = async (current: () => boolean): Promise<Found | string> => {
    if (state.source === "typed" || state.source === "list") {
      choices?.parts([], 1);
    }
    if (state.source === "typed") {
      return { words: typedWords(state.text), sentences: [] };
    }
    if (state.source === "list") {
      if (state.list === "") {
        return "Choose a List or HSK band.";
      }
      if (tooLarge(container, state.list)) {
        return "This List is too long for one worksheet. Choose a part.";
      }
      const list = await sources.list(state.list);

      return list === undefined
        ? "This List could not be loaded. Try again or choose another."
        : fromList(list, false);
    }
    if (state.source === "mine") {
      const lists = await ownShelf();
      if (!current()) {
        return "";
      }
      if (lists === undefined) {
        ownLists?.hide();
        choices?.parts([], 1);

        return "Sign in to print from your Lists.";
      }
      const path = ownLists?.lists(lists, state.mine) ?? state.mine;
      // The first List stands in for none chosen, and the address says so, so
      // a copied link prints this sheet.
      if (path !== state.mine) {
        state = { ...state, mine: path };
        address();
      }
      if (path === "") {
        choices?.parts([], 1);

        return "You have no Lists of your own yet. Make one on Your Lists.";
      }
      const list = await sources.ownList(path);
      if (!current()) {
        return "";
      }
      if (list === undefined) {
        choices?.parts([], 1);

        return "Could not load this List. Try again or choose another.";
      }

      return fromList(list, true);
    }
    if (material === "sentences") {
      const saved = await savedSentences();
      if (!current()) {
        return "";
      }
      if (saved === undefined) {
        choices?.hide();
        choices?.parts([], 1);

        return "Sign in to create a worksheet from your saved sentences.";
      }
      choices?.signedIn();

      const { words: sentences, suffix } = inParts(saved, "");

      return { words: [], sentences, suffix };
    }
    const all = await savedWords();
    if (!current()) {
      return "";
    }
    if (all === undefined) {
      choices?.hide();

      return "Sign in to create a sheet from your saved words.";
    }
    choices?.tags(savedTags(all), all.length, state.tag);
    const tagged = taggedWords(all, state.tag, state.band);
    if (tagged.length === 0 && (state.tag !== "" || state.band !== "")) {
      choices?.parts([], 1);

      return state.tag === ""
        ? `You have no saved words at ${state.band}.`
        : state.band === ""
          ? `No saved words tagged “${state.tag}”.`
          : `You have no saved words tagged “${state.tag}” at ${state.band}.`;
    }
    const narrowed = [state.tag, state.band].filter((one) => one !== "");

    return {
      ...inParts(tagged, narrowed.map((one) => ` · ${one}`).join("")),
      sentences: [],
    };
  };

  const draw = async (): Promise<void> => {
    drawing += 1;
    const mine = drawing;
    const asked = state;
    paper(asked.paper);
    fitPreview();
    const nothing = kind.nothingAsked(asked);
    if (nothing !== undefined) {
      replaceChildren(sheet, []);
      say(nothing);

      return;
    }
    say("Preparing the sheet…");

    const found = await words(() => mine === drawing);
    if (mine !== drawing) {
      return;
    }
    if (
      typeof found !== "string" &&
      (material === "sentences" ? found.sentences : found.words).length === 0
    ) {
      replaceChildren(sheet, []);
      say(
        material === "sentences"
          ? asked.source === "saved"
            ? "You have no saved sentences yet. Save some from the 例句 pages."
            : "There are no sentences to practise."
          : asked.source === "typed"
            ? "Type or paste the characters or words you want to practise."
            : "There are no words to practise.",
      );

      return;
    }
    const drawn =
      typeof found === "string"
        ? found
        : await kind.draw({
            state: asked,
            words: found.words,
            sentences: found.sentences,
            entries,
            labels: {
              title: `${kind.title(asked, found.list)}${found.suffix ?? ""}`,
              site: siteLabel,
              address: location.href,
              origin: location.origin,
            },
          });
    if (mine !== drawing) {
      return;
    }
    if (typeof drawn === "string") {
      replaceChildren(sheet, []);
      say(drawn);

      return;
    }
    replaceChildren(sheet, drawn.nodes);
    say(drawn.status);
  };

  const random = properties.random ?? Math.random;
  const address = (): void => {
    const fragment = kind.fragment(state);
    history.replaceState(
      null,
      "",
      `${location.pathname}${location.search}${fragment === "" ? "" : `#${fragment}`}`,
    );
  };
  // Every way the settings arrive goes through here: a control, the address
  // the page opened on, and an address changed by hand. A shuffle with no
  // seed is given one before anything is drawn, and the address is written
  // so a kept link deals that order again.
  const settle = (): void => {
    if (state.order === "shuffled" && state.seed === "") {
      state = { ...state, seed: freshSeed(random) };
    }
    write(state);
    address();
    void draw();
  };
  const changed = (): void => {
    state = readControls(container, kind, state);
    settle();
  };
  const typed = debounced(changed, 400);

  container.addEventListener("change", (event) => {
    if (!(event.target instanceof HTMLTextAreaElement)) {
      changed();
    }
  });
  slot(container, "text")?.addEventListener("input", typed);

  slot(container, "reshuffle")?.addEventListener("click", () => {
    state = { ...state, seed: freshSeed(random) };
    changed();
  });
  slot(container, "print")?.addEventListener("click", () => {
    window.print();
  });
  const copyLink = slot(container, "copy-link");
  if (copyLink instanceof HTMLButtonElement) {
    wireCopyButton(copyLink, () => plainFlavours(location.href));
  }
  for (const example of container.querySelectorAll<HTMLElement>(
    "[data-cb-example]",
  )) {
    example.addEventListener("click", () => {
      state = {
        ...state,
        source: "typed",
        text: example.dataset["cbExample"] ?? "",
      };
      write(state);
      changed();
    });
  }

  window.addEventListener("hashchange", () => {
    state = kind.read(location.hash);
    settle();
  });
  new ResizeObserver(fitPreview).observe(preview);

  settle();
}

/**
 * What a sheet is called: a List's name, in both languages where it has two,
 * the name of the reader's own List, or the kind's own title for typed words
 * or saved words.
 * @param titles - The kind's title for typed words, and for saved words.
 */
export function sheetTitle(
  state: SheetSourceState,
  list: ListWords | undefined,
  titles: { readonly typed: string; readonly saved: string },
): string {
  if (
    (state.source === "list" || state.source === "mine") &&
    list !== undefined
  ) {
    return list.hanzi === "" ? list.title : `${list.title} · ${list.hanzi}`;
  }

  return state.source === "saved" ? titles.saved : titles.typed;
}

/** "1 page" or "3 pages", and the same for anything else counted. */
export function counted(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
